import { useQueryClient } from "@tanstack/react-query";
import { Box, Drawer, IconButton, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { DocumentUploadForm } from "./DocumentUploadForm";
import type { DocumentEntityType, DocumentType } from "@/api/types";

interface Props {
  open: boolean;
  onClose: () => void;
  entityType: DocumentEntityType;
  entityId: string;
  documentType?: DocumentType;
  /** Invalidated on successful upload so the caller's tables refetch
   * and the new document_id surfaces in their next render. Common
   * patterns: ["sites", siteId, "clinops", daCode] for ClinOps view
   * tables; ["documents"] for the documents app. */
  invalidateKeys?: (string | undefined)[][];
  onUploaded?: (documentId: string) => void;
}

/** Slide-in drawer wrapping the document-upload form. Keeps the user
 * in context (e.g. on the ClinOps site detail) so the table that
 * triggered the upload stays visible after a refetch. */
export function DocumentUploadDrawer({
  open,
  onClose,
  entityType,
  entityId,
  documentType,
  invalidateKeys = [],
  onUploaded,
}: Props) {
  const qc = useQueryClient();
  const handleComplete = (documentId: string) => {
    for (const key of invalidateKeys) {
      qc.invalidateQueries({ queryKey: key });
    }
    onUploaded?.(documentId);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          // The upload form pivots into the rotation / GCP review
          // panels after /complete, which need real estate to render
          // the doc viewer alongside the form. Match the detail drawer.
          sx: { width: { xs: "100%", sm: "75vw" }, maxWidth: 1400 },
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h6">Upload document</Typography>
          <IconButton onClick={onClose} aria-label="Close" size="small">
            <Close />
          </IconButton>
        </Box>
        {/* Remount the form each time the drawer opens so its internal
            state (selected file, title, etc.) resets cleanly between
            uploads. */}
        {open && (
          <DocumentUploadForm
            entityType={entityType}
            entityId={entityId}
            initialDocType={documentType}
            onComplete={handleComplete}
            onCancel={onClose}
          />
        )}
      </Box>
    </Drawer>
  );
}
